import api from './authService';

// ===========================================
// Types
// ===========================================

export interface TenantDashboardSummary {
  this_month_sales: number;
  last_month_sales: number;
  total_sales: number;
  growth_rate: number;
  this_month_transactions: number;
  total_earnings: number;
  this_month_earnings: number;
  pending_earnings: number;
  active_users: number;
}

export interface SalesTrendItem {
  year: number;
  month: number;
  label: string;
  sales: number;
  count: number;
}

export interface TenantTransaction {
  id: number;
  transaction_date: string | null;
  product_name: string | null;
  customer_name: string | null;
  customer_email: string | null;
  plan_name: string | null;
  gross_amount: number;
  status: string;
  payment_source: string;
}

export interface TenantPayout {
  id: number;
  period_start: string | null;
  period_end: string | null;
  net_amount: number;
  status: string;
  paid_date: string | null;
  payout_method: string;
  created_at: string | null;
}

export interface TenantProduct {
  id: number;
  product_id: number;
  product_name: string;
  product_code: string;
  custom_name: string | null;
  is_enabled: number;
  total_sales: number;
  this_month_sales: number;
  transaction_count: number;
}

export interface TenantUser {
  id: number;
  email: string;
  full_name: string | null;
  role: string;
  is_active: number;
  is_license_valid: boolean;
  license_valid_until: string | null;
  created_at: string | null;
}

// ===========================================
// API
// ===========================================

export const getTenantDashboardSummary = async (): Promise<TenantDashboardSummary> => {
  const response = await api.get<TenantDashboardSummary>('/my-tenant/dashboard/summary');
  return response.data;
};

export const getTenantSalesTrend = async (months: number = 6): Promise<SalesTrendItem[]> => {
  const response = await api.get<{ data: SalesTrendItem[] }>(`/my-tenant/dashboard/sales-trend?months=${months}`);
  return response.data.data;
};

export const getTenantRecentTransactions = async (limit: number = 10): Promise<TenantTransaction[]> => {
  const response = await api.get<{ data: TenantTransaction[] }>(`/my-tenant/dashboard/recent-transactions?limit=${limit}`);
  return response.data.data;
};

export const getTenantPayoutHistory = async (limit: number = 20): Promise<{
  data: TenantPayout[];
  summary: { total_paid: number; total_pending: number };
}> => {
  const response = await api.get(`/my-tenant/dashboard/payout-history?limit=${limit}`);
  return response.data;
};

export const getTenantProducts = async (): Promise<TenantProduct[]> => {
  const response = await api.get<{ data: TenantProduct[] }>('/my-tenant/dashboard/products');
  return response.data.data;
};

export const getTenantUsers = async (skip: number = 0, limit: number = 50): Promise<{
  total: number;
  data: TenantUser[];
}> => {
  const response = await api.get(`/my-tenant/dashboard/users?skip=${skip}&limit=${limit}`);
  return response.data;
};
